(() => {
  if (window.AmerceProductCartSync) return;

  let refreshTimer = null;
  let refreshController = null;

  const getNumber = (value, fallback) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  };

  const normalizeQuantity = (input, value) => {
    const minimum = getNumber(input.min, 1);
    const maximum = input.max
      ? getNumber(input.max, Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
    const step = Math.max(1, getNumber(input.step, 1));

    let next = getNumber(value, minimum);
    next = Math.max(minimum, next);

    if (Number.isFinite(maximum)) {
      next = Math.min(maximum, next);
    }

    const stepCount = Math.round((next - minimum) / step);
    next = minimum + Math.max(0, stepCount) * step;

    if (Number.isFinite(maximum)) {
      next = Math.min(maximum, next);
    }

    return Math.max(minimum, next);
  };

  const refreshMainProductForm = async (root, signal) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.closest("quick-add-modal")) return;

    const sectionId = root.dataset.sectionId;
    const productUrl = root.dataset.productUrl;
    const currentButtons = root.querySelector(".product-buy-buttons");

    if (!sectionId || !productUrl || !currentButtons) return;

    const currentQuantity =
      root.querySelector("[data-quantity-input]")?.value;

    const requestUrl = new URL(
      productUrl,
      window.location.origin,
    );

    const variantId = root.dataset.selectedVariantId;

    if (variantId) {
      requestUrl.searchParams.set("variant", variantId);
    }

    requestUrl.searchParams.set("section_id", sectionId);

    const response = await fetch(requestUrl.toString(), {
      signal,
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Main product refresh failed: ${response.status}`,
      );
    }

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(
      html,
      "text/html",
    );

    const updatedRoot = parsed.getElementById(
      `MainProduct-${sectionId}`,
    );

    const nextButtons = updatedRoot?.querySelector(
      ".product-buy-buttons",
    );

    if (!nextButtons) return;

    /*
     * Vá tại chỗ thay vì currentButtons.replaceWith(nextButtons) — swap
     * nguyên node khiến nút Add to cart bị giật/nháy đúng lúc cart drawer
     * đang mở (mất hover/focus, huỷ animation đang chạy dở). Sau khi thêm
     * giỏ hàng chỉ có min/max số lượng và trạng thái còn hàng có thể đổi,
     * nên chỉ cập nhật đúng hai phần đó, giữ nguyên node nút.
     */
    const nextQuantityInput = nextButtons.querySelector(
      "[data-quantity-input]",
    );
    const currentQuantityInput = currentButtons.querySelector(
      "[data-quantity-input]",
    );

    if (currentQuantityInput && nextQuantityInput) {
      ["min", "max", "step"].forEach((attr) => {
        if (nextQuantityInput.hasAttribute(attr)) {
          currentQuantityInput.setAttribute(
            attr,
            nextQuantityInput.getAttribute(attr),
          );
        } else {
          currentQuantityInput.removeAttribute(attr);
        }
      });

      currentQuantityInput.value = String(
        normalizeQuantity(
          currentQuantityInput,
          currentQuantity ?? currentQuantityInput.value,
        ),
      );
    }

    const currentVariantInput = currentButtons.querySelector(
      "[data-variant-id-input]",
    );
    const nextVariantInput = nextButtons.querySelector(
      "[data-variant-id-input]",
    );

    if (currentVariantInput && nextVariantInput) {
      currentVariantInput.toggleAttribute(
        "disabled",
        nextVariantInput.disabled,
      );
    }

    const currentSubmit = currentButtons.querySelector(
      "[data-add-to-cart]",
    );
    const nextSubmit = nextButtons.querySelector(
      "[data-add-to-cart]",
    );

    if (currentSubmit && nextSubmit) {
      currentSubmit.toggleAttribute(
        "disabled",
        nextSubmit.disabled,
      );

      const currentText = currentSubmit.querySelector(
        "[data-add-to-cart-text]",
      );
      const nextText = nextSubmit.querySelector(
        "[data-add-to-cart-text]",
      );

      if (currentText && nextText) {
        currentText.textContent = nextText.textContent;
      }
    }

    root.syncAllQuantityControls?.();
    root.updateAddToCartPrice?.();

    if (
      typeof window.Shopify?.PaymentButton?.init ===
      "function"
    ) {
      window.Shopify.PaymentButton.init();
    }
  };

  const refreshMainProducts = async () => {
    const roots = Array.from(
      document.querySelectorAll(
        "main-product[data-section-id][data-product-url]",
      ),
    ).filter(
      (root) =>
        !root.closest("quick-add-modal") &&
        root.querySelector(".product-buy-buttons"),
    );

    if (!roots.length) return;

    refreshController?.abort();
    refreshController = new AbortController();

    const results = await Promise.allSettled(
      roots.map((root) =>
        refreshMainProductForm(
          root,
          refreshController.signal,
        ),
      ),
    );

    results.forEach((result) => {
      if (
        result.status === "rejected" &&
        result.reason?.name !== "AbortError"
      ) {
        console.error(result.reason);
      }
    });
  };

  const scheduleRefresh = () => {
    window.clearTimeout(refreshTimer);

    refreshTimer = window.setTimeout(() => {
      refreshMainProducts();
    }, 120);
  };

  ["product:added", "cart:rendered", "cart:updated"].forEach(
    (eventName) => {
      document.addEventListener(
        eventName,
        scheduleRefresh,
      );
    },
  );

  window.AmerceProductCartSync = {
    refresh: refreshMainProducts,
    schedule: scheduleRefresh,
  };
})();

(() => {
  if (window.productCardQuickAddInitialized) return;

  window.productCardQuickAddInitialized = true;

  document.addEventListener(
    "submit",
    async (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;

      const form = event.target.closest(
        'form[data-type="add-to-cart-form"]',
      );

      if (!form) return;

      /*
       * Quick View có handler Add to cart riêng để đóng Quick View
       * trước khi mở cart drawer. Không để listener toàn cục bắt form này.
       */
      if (form.closest("quick-view-modal")) return;

      const submitter =
        event.submitter instanceof HTMLElement
          ? event.submitter
          : null;

      /*
       * Chỉ xử lý nút Add to cart. Không chặn accelerated checkout
       * hoặc những submitter khác nằm trong cùng product form.
       */
      if (
        submitter &&
        !submitter.matches(
          '[data-add-to-cart], button[name="add"], input[name="add"]',
        )
      ) {
        return;
      }

      const cartBehavior =
        form.dataset.cartBehavior || "page";

      const cartDrawer = document.querySelector(
        "cart-drawer",
      );

      /*
       * Không có cart-drawer trên trang (hiếm gặp) thì để
       * Shopify submit form theo cách mặc định.
       */
      if (!cartDrawer) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitButton =
        submitter ||
        form.querySelector("[data-add-to-cart]");

      const errorElement =
        form.querySelector("[data-add-to-cart-error]") ||
        form
          .closest(".product-buy-buttons")
          ?.querySelector("[data-product-form-status]") ||
        null;

      const fallbackError =
        form.dataset.addError ||
        "Unable to add this product to your cart. Please try again.";

      setLoading(submitButton, true);
      clearError(errorElement);

      try {
        const formData = new FormData(form);

        formData.set(
          "sections",
          form.dataset.cartSectionId || "cart-drawer",
        );

        formData.set(
          "sections_url",
          window.location.pathname,
        );

        const cartAddUrl =
          form.dataset.cartAddUrl ||
          `${window.Shopify?.routes?.root || "/"}cart/add.js`;

        const response = await fetch(cartAddUrl, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
          redirect: "follow",
          body: formData,
        });

        const result = await parseResponse(response);

        if (!response.ok) {
          throw new Error(
            getResponseError(result, fallbackError),
          );
        }

        /*
         * cart/add.js phải trả JSON. Nếu request bị chuyển sang
         * password page, challenge page hoặc một trang HTML khác,
         * không bao giờ in nguyên HTML/script ra giao diện.
         */
        if (!result || typeof result !== "object") {
          throw new Error(fallbackError);
        }

        document.dispatchEvent(
          new CustomEvent("product:added", {
            detail: {
              response: result,
              form,
              open: cartBehavior === "drawer",
            },
          }),
        );
      } catch (error) {
        console.error(error);

        showError(
          errorElement,
          isSafeMessage(error?.message)
            ? error.message
            : fallbackError,
        );
      } finally {
        setLoading(submitButton, false);
      }
    },
    true,
  );

  async function parseResponse(response) {
    const text = await response.text();

    if (!text.trim()) return {};

    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function getResponseError(result, fallbackError) {
    const candidate =
      result?.description ||
      result?.message ||
      result?.errors;

    if (typeof candidate === "string" && isSafeMessage(candidate)) {
      return candidate.trim();
    }

    if (candidate && typeof candidate === "object") {
      const firstMessage = Object.values(candidate)
        .flat()
        .find(
          (message) =>
            typeof message === "string" &&
            isSafeMessage(message),
        );

      if (firstMessage) return firstMessage.trim();
    }

    return fallbackError;
  }

  function isSafeMessage(message) {
    if (typeof message !== "string") return false;

    const normalized = message.trim();

    if (!normalized || normalized.length > 500) {
      return false;
    }

    return !/(?:<!doctype|<html|<body|<script|<\/script>|navigator\.language|data-i18n)/i.test(
      normalized,
    );
  }

  function setLoading(button, loading) {
    if (!(button instanceof HTMLElement)) return;

    button.toggleAttribute("disabled", loading);
    button.setAttribute(
      "aria-busy",
      String(loading),
    );

    button.classList.toggle(
      "is-loading",
      loading,
    );
  }

  function clearError(errorElement) {
    if (!(errorElement instanceof HTMLElement)) {
      return;
    }

    errorElement.textContent = "";
    errorElement.hidden = true;
  }

  function showError(errorElement, message) {
    if (!(errorElement instanceof HTMLElement)) {
      return;
    }

    errorElement.textContent = message;
    errorElement.hidden = false;
  }
})();

class QuickAddModal extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.activeTrigger = null;
    this.productCache = new Map();
    this.pendingRequests = new Map();
    this.cacheGeneration = 0;
    this.prefetchRefreshTimer = null;

    this.boundDocumentClick = this.onDocumentClick.bind(this);
    this.boundDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.boundPrefetch = this.onPrefetch.bind(this);
    this.boundClick = this.onClick.bind(this);
    this.boundProductAdded = this.onProductAdded.bind(this);
    this.boundCartChanged = this.onCartChanged.bind(this);

    this.quickAddObserver = null;
  }

  connectedCallback() {
    if (this.dataset.initialized === "true") return;

    this.dataset.initialized = "true";

    this.content = this.querySelector(
      "[data-quick-add-content]",
    );

    this.error = this.querySelector(
      "[data-quick-add-error]",
    );

    this.dialog = this.querySelector(
      '[role="dialog"]',
    );

    this.closeButton = this.querySelector(
      "[data-quick-add-close-button]",
    );

    document.addEventListener(
      "click",
      this.boundDocumentClick,
    );

    document.addEventListener(
      "keydown",
      this.boundDocumentKeydown,
    );

    document.addEventListener(
      "pointerover",
      this.boundPrefetch,
    );

    document.addEventListener(
      "focusin",
      this.boundPrefetch,
    );

    document.addEventListener(
      "product:added",
      this.boundProductAdded,
    );

    document.addEventListener(
      "cart:rendered",
      this.boundCartChanged,
    );

    document.addEventListener(
      "cart:updated",
      this.boundCartChanged,
    );

    this.addEventListener(
      "click",
      this.boundClick,
    );

    this.initializePrefetchObserver();
  }

  disconnectedCallback() {
    document.removeEventListener(
      "click",
      this.boundDocumentClick,
    );

    document.removeEventListener(
      "keydown",
      this.boundDocumentKeydown,
    );

    document.removeEventListener(
      "pointerover",
      this.boundPrefetch,
    );

    document.removeEventListener(
      "focusin",
      this.boundPrefetch,
    );

    document.removeEventListener(
      "product:added",
      this.boundProductAdded,
    );

    document.removeEventListener(
      "cart:rendered",
      this.boundCartChanged,
    );

    document.removeEventListener(
      "cart:updated",
      this.boundCartChanged,
    );

    this.removeEventListener(
      "click",
      this.boundClick,
    );

    this.quickAddObserver?.disconnect();
    window.clearTimeout(this.prefetchRefreshTimer);
  }

  initializePrefetchObserver() {
    const triggers = document.querySelectorAll(
      "[data-quick-add-open][data-product-url]",
    );

    if (!triggers.length) return;

    if (!("IntersectionObserver" in window)) {
      triggers.forEach((trigger) => {
        this.prefetchTrigger(trigger);
      });

      return;
    }

    this.quickAddObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          this.prefetchTrigger(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        rootMargin: "300px 0px",
      },
    );

    triggers.forEach((trigger) => {
      this.quickAddObserver.observe(trigger);
    });
  }

  onPrefetch(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      "[data-quick-add-open][data-product-url]",
    );

    if (!trigger) return;

    this.prefetchTrigger(trigger);
  }

  prefetchTrigger(trigger) {
    const productUrl = trigger.dataset.productUrl;

    if (!productUrl) return;

    this.fetchProductMarkup(productUrl).catch(() => {
      // Prefetch chạy nền nên không cần hiển thị lỗi.
    });
  }

  async onDocumentClick(event) {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest(
      "[data-quick-add-open]",
    );

    if (!trigger) return;

    const productUrl = trigger.dataset.productUrl;

    if (!productUrl) return;

    event.preventDefault();

    await this.open(trigger, productUrl);
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    const closeButton = event.target.closest(
      "[data-quick-add-close]",
    );

    if (!closeButton) return;

    event.preventDefault();
    this.close();
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  onProductAdded(event) {
    this.invalidateCache();

    if (!this.isOpen) return;

    const form = event.detail?.form;

    if (form && !this.contains(form)) return;

    this.close(false);
  }

  onCartChanged() {
    this.invalidateCache();
  }

  invalidateCache() {
    this.cacheGeneration += 1;
    this.productCache.clear();
    this.pendingRequests.clear();

    window.clearTimeout(this.prefetchRefreshTimer);

    this.prefetchRefreshTimer = window.setTimeout(() => {
      this.prefetchVisibleTriggers();
    }, 160);
  }

  prefetchVisibleTriggers() {
    document
      .querySelectorAll(
        "[data-quick-add-open][data-product-url]",
      )
      .forEach((trigger) => {
        const rect = trigger.getBoundingClientRect();
        const inPrefetchRange =
          rect.bottom >= -300 &&
          rect.top <= window.innerHeight + 300;

        if (inPrefetchRange) {
          this.prefetchTrigger(trigger);
        }
      });
  }

  getCacheKey(productUrl) {
    const url = new URL(
      productUrl,
      window.location.origin,
    );

    url.hash = "";

    return `${url.pathname}${url.search}`;
  }

  getSectionUrl(productUrl) {
    const url = new URL(
      productUrl,
      window.location.origin,
    );

    url.searchParams.set(
      "section_id",
      "quick-add-product",
    );

    return url;
  }

  async fetchProductMarkup(productUrl) {
    const cacheKey = this.getCacheKey(productUrl);
    const generation = this.cacheGeneration;

    if (this.productCache.has(cacheKey)) {
      return this.productCache.get(cacheKey);
    }

    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    const request = fetch(
      this.getSectionUrl(productUrl),
      {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      },
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            `Quick Add request failed: ${response.status}`,
          );
        }

        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(
          html,
          "text/html",
        );

        const productContent = parsed.querySelector(
          "[data-quick-add-product]",
        );

        if (!productContent) {
          throw new Error(
            "Quick Add content was not found.",
          );
        }

        const markup = productContent.outerHTML;

        /*
         * Chỉ cache response nếu cart chưa đổi trong lúc request.
         * Tránh lưu lại HTML có max quantity cũ.
         */
        if (generation === this.cacheGeneration) {
          this.productCache.set(cacheKey, markup);
        }

        return markup;
      })
      .finally(() => {
        if (
          this.pendingRequests.get(cacheKey) === request
        ) {
          this.pendingRequests.delete(cacheKey);
        }
      });

    this.pendingRequests.set(cacheKey, request);

    return request;
  }

  async open(trigger, productUrl) {
    if (!this.content) return;

    this.activeTrigger = trigger;
    this.clearError();

    /*
     * Không mở modal ở đây.
     * Chờ content tải và render hoàn chỉnh trước.
     */
    trigger.disabled = true;
    trigger.setAttribute("aria-busy", "true");

    try {
      const markup = await this.fetchProductMarkup(
        productUrl,
      );

      this.renderProduct(markup);

      trigger.setAttribute(
        "aria-expanded",
        "true",
      );

      this.hidden = false;
      this.setAttribute("aria-hidden", "false");

      document.documentElement.classList.add(
        "overflow-hidden",
      );

      document.body.classList.add(
        "overflow-hidden",
      );

      requestAnimationFrame(() => {
        this.classList.add("show");
        this.isOpen = true;

        this.closeButton?.focus({
          preventScroll: true,
        });
      });
    } catch (error) {
      console.error(error);

      this.showError(
        this.dataset.errorMessage ||
          "Unable to load this product.",
      );
    } finally {
      trigger.disabled = false;
      trigger.removeAttribute("aria-busy");
    }
  }

  renderProduct(markup) {
    const template = document.createElement("template");

    template.innerHTML = markup.trim();

    const productContent =
      template.content.firstElementChild;

    if (!productContent) {
      throw new Error(
        "Quick Add content is invalid.",
      );
    }

    this.content.replaceChildren(productContent);

    this.initializePaymentButtons();
  }

  close(restoreFocus = true) {
    this.classList.remove("show");
    this.setAttribute("aria-hidden", "true");

    this.isOpen = false;

    document.documentElement.classList.remove(
      "overflow-hidden",
    );

    document.body.classList.remove(
      "overflow-hidden",
    );

    if (this.activeTrigger) {
      this.activeTrigger.setAttribute(
        "aria-expanded",
        "false",
      );
    }

    window.setTimeout(() => {
      if (this.isOpen) return;

      this.hidden = true;
      this.content?.replaceChildren();
      this.clearError();
    }, 300);

    if (
      restoreFocus &&
      this.activeTrigger instanceof HTMLElement
    ) {
      this.activeTrigger.focus({
        preventScroll: true,
      });
    }
  }

  initializePaymentButtons() {
    if (
      typeof window.Shopify?.PaymentButton?.init ===
      "function"
    ) {
      window.Shopify.PaymentButton.init();
    }
  }

  clearError() {
    if (!this.error) return;

    this.error.textContent = "";
    this.error.hidden = true;
  }

  showError(message) {
    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = false;
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.querySelectorAll(
        [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])',
        ].join(","),
      ),
    ).filter((element) => {
      return (
        !element.hidden &&
        element.offsetParent !== null
      );
    });

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      event.preventDefault();
      last.focus();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("quick-add-modal")) {
  customElements.define(
    "quick-add-modal",
    QuickAddModal,
  );
}